import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import io from 'socket.io-client';
import axios from 'axios';
import { FiSend, FiUser, FiHash, FiPlus, FiTrash2, FiEdit2, FiAtSign } from 'react-icons/fi';
import Toast from './Toast';

import { API_URL, SOCKET_URL, getAssetUrl } from '../config';

/** Highlight @username in channel message text */
function renderMessageWithMentions(text, isOwnMessage) {
  if (!text) return null;
  const parts = [];
  const re = /@([a-zA-Z0-9_.-]+)/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(<span key={`t-${k++}`}>{text.slice(last, m.index)}</span>);
    }
    parts.push(
      <span
        key={`m-${k++}`}
        className={
          isOwnMessage
            ? 'font-bold text-amber-100 underline decoration-amber-200/80'
            : 'font-bold text-[#3c79bb] bg-[#3c79bb]/12 rounded px-0.5'
        }
      >
        @{m[1]}
      </span>
    );
    last = m.lastIndex;
  }
  if (last < text.length) {
    parts.push(<span key={`t-${k++}`}>{text.slice(last)}</span>);
  }
  return parts.length > 0 ? parts : text;
}

function Chat() {
  const { user } = useAuth();
  const {
    clearSourceNotification,
    unreadCountFor,
    mentionCountFor,
    setActiveChat,
    joinChatRoomForNotifications,
  } = useNotifications();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentRoom, setCurrentRoom] = useState('general');
  const [currentRecipient, setCurrentRecipient] = useState(null);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDesc, setNewChannelDesc] = useState('');
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const currentRoomRef = useRef(currentRoom);
  const currentRecipientRef = useRef(currentRecipient);
  const userRef = useRef(user);

  // Keep refs in sync
  useEffect(() => {
    currentRoomRef.current = currentRoom;
  }, [currentRoom]);

  useEffect(() => {
    currentRecipientRef.current = currentRecipient;
  }, [currentRecipient]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const newSocket = io(SOCKET_URL, {
      auth: { token },
    });

    newSocket.on('connect', () => {
      const room = currentRoomRef.current;
      const recip = currentRecipientRef.current;
      if (!recip) {
        newSocket.emit('join-room', room || 'general');
      }
      loadChannels();
      loadUsers();
      // Do not call loadChatHistory() here — this handler is created once (effect []) and would
      // always use the initial loadChatHistory closure (wrong room/DM). History is loaded by
      // the effects that depend on [socket, currentRoom] and [currentRecipient].
    });

    const isDmPayload = (data) =>
      data.recipientId !== undefined &&
      data.recipientId !== null &&
      data.recipientId !== '' &&
      String(data.recipientId).trim() !== '';

    const matchesCurrentView = (data) => {
      const room = currentRoomRef.current;
      const recipient = currentRecipientRef.current;
      const me = userRef.current;
      const meId = me?.id != null ? parseInt(String(me.id), 10) : NaN;

      if (isDmPayload(data)) {
        if (!recipient) return false;
        const rid = parseInt(String(data.recipientId), 10);
        const fromUser = parseInt(String(data.userId), 10);
        const otherId = parseInt(String(recipient.id), 10);
        if (Number.isNaN(rid) || Number.isNaN(fromUser) || Number.isNaN(otherId) || Number.isNaN(meId)) {
          return false;
        }
        return (
          (fromUser === otherId && rid === meId) ||
          (fromUser === meId && rid === otherId)
        );
      }

      if (recipient) return false;
      const msgRoom = data.room != null ? String(data.room) : '';
      if (!room) return false;
      return msgRoom === String(room);
    };

    const matchesViewForMeta = (payload) => {
      const room = currentRoomRef.current;
      const recipient = currentRecipientRef.current;
      const me = userRef.current;
      const meId = me?.id != null ? parseInt(String(me.id), 10) : NaN;

      const hasRecipient =
        payload.recipientId !== undefined &&
        payload.recipientId !== null &&
        payload.recipientId !== '' &&
        String(payload.recipientId).trim() !== '';

      if (hasRecipient) {
        if (!recipient) return false;
        const rid = parseInt(String(payload.recipientId), 10);
        const otherId = parseInt(String(recipient.id), 10);
        if (Number.isNaN(rid) || Number.isNaN(otherId) || Number.isNaN(meId)) return false;
        return rid === meId || rid === otherId;
      }

      if (recipient) return false;
      const msgRoom = payload.room != null ? String(payload.room) : '';
      if (!room) return false;
      return msgRoom === String(room);
    };

    const isDuplicateIncoming = (prev, data) => {
      if (data.id != null && prev.some((m) => m.id != null && String(m.id) === String(data.id))) {
        return true;
      }
      const t = new Date(data.timestamp || 0).getTime();
      return prev.some((m) => {
        if (m.message !== data.message) return false;
        const uid = m.userId != null ? parseInt(String(m.userId), 10) : NaN;
        const duid = data.userId != null ? parseInt(String(data.userId), 10) : NaN;
        if (uid !== duid) return false;
        const mRoom = m.room != null ? String(m.room) : '';
        const dRoom = data.room != null ? String(data.room) : '';
        if (mRoom !== dRoom) return false;
        const mR = m.recipientId != null ? parseInt(String(m.recipientId), 10) : null;
        const dR = data.recipientId != null ? parseInt(String(data.recipientId), 10) : null;
        if (mR !== dR) return false;
        return Math.abs(new Date(m.timestamp || 0).getTime() - t) < 5000;
      });
    };

    const handleReceiveMessage = (data) => {
      setMessages((prev) => {
        if (!matchesCurrentView(data)) return prev;
        if (isDuplicateIncoming(prev, data)) return prev;
        return [...prev, data];
      });
    };

    const handleMessageUpdated = (data) => {
      setMessages((prev) => {
        if (!matchesViewForMeta(data)) return prev;
        return prev.map((m) =>
          String(m.id) === String(data.id)
            ? { ...m, message: data.message, edited_at: data.edited_at }
            : m
        );
      });
    };

    const handleMessageDeleted = (data) => {
      setMessages((prev) => {
        if (!matchesViewForMeta(data)) return prev;
        return prev.filter((m) => String(m.id) !== String(data.id));
      });
    };

    newSocket.on('receive-message', handleReceiveMessage);
    newSocket.on('chat-message-updated', handleMessageUpdated);
    newSocket.on('chat-message-deleted', handleMessageDeleted);

    setSocket(newSocket);

    return () => {
      newSocket.off('receive-message', handleReceiveMessage);
      newSocket.off('chat-message-updated', handleMessageUpdated);
      newSocket.off('chat-message-deleted', handleMessageDeleted);
      newSocket.close();
    };
  }, []);

  useEffect(() => {
    if (socket && currentRoom && !currentRecipient) {
      socket.emit('join-room', currentRoom);
      loadChatHistory();
    }
  }, [currentRoom, socket]);

  useEffect(() => {
    if (currentRecipient) {
      loadChatHistory();
    }
  }, [currentRecipient]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update active chat and clear its notifications whenever the selection changes
  useEffect(() => {
    if (currentRecipient) {
      setActiveChat({ type: 'dm', userId: currentRecipient.id });
      clearSourceNotification(`dm:${currentRecipient.id}`);
    } else if (currentRoom) {
      setActiveChat({ type: 'channel', name: currentRoom });
      clearSourceNotification(`channel:${currentRoom}`);
    }
  }, [currentRecipient, currentRoom, setActiveChat, clearSourceNotification]);

  // Clear active chat on unmount
  useEffect(() => {
    return () => setActiveChat(null);
  }, [setActiveChat]);

  const loadChannels = async () => {
    try {
      const response = await axios.get(`${API_URL}/chat/channels`);
      const list = response.data.channels || [];
      setChannels(list);
      list.forEach((ch) => {
        if (ch?.name) joinChatRoomForNotifications(ch.name);
      });
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/chat`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback: try the admin endpoint if chat endpoint fails
      try {
        const fallbackResponse = await axios.get(`${API_URL}/users`);
        setUsers(fallbackResponse.data.users.filter(u => u.id !== user?.id) || []);
      } catch (fallbackError) {
        console.error('Error loading users (fallback):', fallbackError);
        setUsers([]);
      }
    }
  };

  const loadChatHistory = async () => {
    try {
      let response;
      if (currentRecipient) {
        response = await axios.get(`${API_URL}/chat/history?recipientId=${currentRecipient.id}&limit=50`);
      } else {
        response = await axios.get(`${API_URL}/chat/history?room=${currentRoom}&limit=50`);
      }
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChannelClick = (channelName) => {
    if (socket && currentRoom && currentRoom !== channelName) {
      socket.emit('leave-room', currentRoom);
    }
    setMessages([]);
    setCurrentRecipient(null);
    setCurrentRoom(channelName);
    clearSourceNotification(`channel:${channelName}`);
    setActiveChat({ type: 'channel', name: channelName });
  };

  const handleUserClick = (recipient) => {
    if (socket && currentRoom) {
      socket.emit('leave-room', currentRoom);
    }
    setMessages([]);
    setCurrentRecipient(recipient);
    setCurrentRoom(null);
    clearSourceNotification(`dm:${recipient.id}`);
    setActiveChat({ type: 'dm', userId: recipient.id });
  };

  const createChannel = async (e) => {
    e.preventDefault();
    if (!newChannelName.trim()) {
      setToast({ message: 'Channel name is required', type: 'error' });
      return;
    }

    try {
      await axios.post(`${API_URL}/chat/channels`, {
        name: newChannelName.trim(),
        description: newChannelDesc.trim()
      });
      setToast({ message: 'Channel created successfully!', type: 'success' });
      setShowChannelModal(false);
      setNewChannelName('');
      setNewChannelDesc('');
      loadChannels();
      handleChannelClick(newChannelName.trim());
    } catch (error) {
      console.error('Error creating channel:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.error || error.message || 'Error creating channel';
      setToast({ message: errorMessage, type: 'error' });
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !socket) return;

    const messageText = newMessage.trim();
    const messageData = {
      message: messageText,
      room: currentRecipient ? null : currentRoom,
      recipientId: currentRecipient ? currentRecipient.id : null,
    };

    // Optimistic update - add message immediately
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const optimisticMessage = {
      id: tempId,
      message: messageText,
      user: user?.username,
      userId: user?.id,
      role: user?.role,
      room: currentRecipient ? null : currentRoom,
      recipientId: currentRecipient ? currentRecipient.id : null,
      profileImage: user?.profileImage,
      firstName: user?.firstName,
      lastName: user?.lastName,
      timestamp: new Date().toISOString()
    };
    setMessages((prev) => [...prev, optimisticMessage]);

    socket.emit('send-message', messageData);

    // Save to database
    try {
      const res = await axios.post(`${API_URL}/chat/save`, messageData);
      if (res.data?.id != null) {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, id: res.data.id } : m))
        );
      }
    } catch (error) {
      console.error('Error saving message:', error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter(m => m.id !== tempId));
    }

    setNewMessage('');
    inputRef.current?.focus();
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return getAssetUrl(imagePath);
  };

  const getDisplayName = (msg) => {
    if (!msg) return 'Unknown';
    return msg.firstName || msg.lastName 
      ? `${msg.firstName || ''} ${msg.lastName || ''}`.trim() 
      : (msg.user || 'Unknown');
  };

  const currentChatName = currentRecipient 
    ? `${currentRecipient.firstName || ''} ${currentRecipient.lastName || ''}`.trim() || currentRecipient.username
    : currentRoom;

  const mentionMatch =
    !currentRecipient && newMessage.match(/(?:^|\s)@([a-zA-Z0-9_.-]*)$/);
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : '';
  const mentionCandidates =
    mentionMatch && users.length > 0
      ? users
          .filter((u) =>
            (u.username || '').toLowerCase().startsWith(mentionQuery)
          )
          .slice(0, 8)
      : [];

  const insertMention = (pick) => {
    const lastAt = newMessage.lastIndexOf('@');
    if (lastAt === -1) return;
    const before = newMessage.slice(0, lastAt);
    setNewMessage(`${before}@${pick.username} `);
    inputRef.current?.focus();
  };

  const isPersistedMessageId = (id) =>
    id != null && String(id).indexOf('temp-') !== 0 && !Number.isNaN(Number(id));

  const deleteMessage = async (msgId) => {
    if (!isPersistedMessageId(msgId)) return;
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/chat/messages/${msgId}`);
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(msgId)));
    } catch (e) {
      setToast({ message: e.response?.data?.error || 'Could not delete message', type: 'error' });
    }
  };

  const startEdit = (msg) => {
    setEditingMessageId(msg.id);
    setEditDraft(msg.message || '');
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditDraft('');
  };

  const saveEdit = async (msgId) => {
    const text = editDraft.trim();
    if (!text) return;
    if (!isPersistedMessageId(msgId)) return;
    try {
      await axios.patch(`${API_URL}/chat/messages/${msgId}`, { message: text });
      setEditingMessageId(null);
      setEditDraft('');
    } catch (e) {
      setToast({ message: e.response?.data?.error || 'Could not save edit', type: 'error' });
    }
  };

  const handleDeleteChannel = async (channelId, channelName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the channel "${channelName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/chat/channels/${channelId}`);
      setToast({ message: 'Channel deleted successfully!', type: 'success' });
      loadChannels();
      // If we're in the deleted channel, switch to general
      if (currentRoom === channelName) {
        handleChannelClick('general');
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      setToast({ message: error.response?.data?.error || 'Error deleting channel', type: 'error' });
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] w-full max-w-[100vw] overflow-x-hidden">
      <div className="flex flex-1 overflow-hidden relative w-full max-w-[100vw]">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden animate-fadeIn"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} fixed lg:static inset-y-0 left-0 z-50 w-64 sm:w-72 glass-nav border-r border-white/20 flex flex-col transition-transform duration-300 ease-in-out`}>
        <div className="p-3 sm:p-4 border-b border-white/20">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Chat</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowChannelModal(true)}
                className="p-1.5 sm:p-2 hover:bg-white/50 rounded-xl transition-all duration-200 text-[#3c79bb] hover:shadow-md"
                title="Create Channel"
              >
                <FiPlus className="text-lg sm:text-xl" />
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 sm:p-2 hover:bg-white/50 rounded-xl transition-all duration-200 text-gray-900"
                title="Close Sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Channels */}
          <div className="p-2">
            <div className="text-xs font-bold text-gray-800 uppercase tracking-wider px-2 mb-2">
              Channels
            </div>
            {channels && channels.length > 0 ? channels.map((channel) => {
              const isSelected = !currentRecipient && currentRoom === channel.name;
              const channelUnread = unreadCountFor(`channel:${channel.name}`);
              const mentionUnread = mentionCountFor(channel.name);
              const channelHasUnread = channelUnread > 0;
              const hasMention = mentionUnread > 0;
              return (
              <div
                key={channel.id}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl mb-1 transition-all duration-200 group ${
                  isSelected
                    ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                    : 'hover:bg-white/50 text-gray-900 hover:shadow-md font-medium'
                }`}
              >
                <button
                  onClick={() => handleChannelClick(channel.name)}
                  className="flex-1 flex items-center gap-2 min-w-0"
                >
                  <FiHash className="text-sm flex-shrink-0" />
                  <span className={`text-sm truncate flex-1 min-w-0 text-left ${channelHasUnread || hasMention ? 'font-bold' : 'font-semibold'}`}>{channel.name}</span>
                  <span className="flex flex-shrink-0 items-center gap-1">
                    {hasMention && !isSelected && (
                      <span
                        className="min-w-[1.25rem] h-5 px-1 inline-flex items-center justify-center gap-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full shadow-sm"
                        title="Unread mentions"
                      >
                        <FiAtSign className="w-3 h-3 flex-shrink-0" />
                        {mentionUnread > 99 ? '99+' : mentionUnread}
                      </span>
                    )}
                    {channelHasUnread && !isSelected && (
                      <span className="min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full shadow-sm">
                        {channelUnread > 99 ? '99+' : channelUnread}
                      </span>
                    )}
                  </span>
                </button>
                {user?.role === 'admin' && channel.name !== 'general' && (
                  <button
                    onClick={(e) => handleDeleteChannel(channel.id, channel.name, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-all text-red-600"
                    title="Delete channel"
                  >
                    <FiTrash2 className="text-sm" />
                  </button>
                )}
              </div>
              );
            }) : (
              <div className="px-3 py-2 text-sm text-gray-700 font-medium">No channels yet</div>
            )}
          </div>

          {/* Direct Messages */}
          <div className="p-2 border-t mt-2">
            <div className="text-xs font-bold text-gray-800 uppercase tracking-wider px-2 mb-2">
              Direct Messages
            </div>
            {users && users.length > 0 ? users.map((u) => {
              const isActive = currentRecipient && currentRecipient.id === u.id;
              const dmUnread = unreadCountFor(`dm:${u.id}`);
              const dmHasUnread = dmUnread > 0;
              return (
                <button
                  key={u.id}
                  onClick={() => handleUserClick(u)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl mb-1 transition-all duration-200 min-w-0 ${
                    isActive
                      ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                      : 'hover:bg-white/50 text-gray-900 hover:shadow-md font-medium'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {u.profileImage ? (
                      <img
                        src={getImageUrl(u.profileImage)}
                        alt={u.username}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-xs font-semibold">
                        {(u.firstName && u.firstName[0]) || (u.username && u.username[0]) || '?'}
                      </div>
                    )}
                  </div>
                  <span className={`text-sm truncate flex-1 min-w-0 text-left ${dmHasUnread ? 'font-extrabold' : 'font-bold'}`}>
                    {u.firstName || u.lastName 
                      ? `${u.firstName || ''} ${u.lastName || ''}`.trim() 
                      : u.username}
                  </span>
                  {dmHasUnread && !isActive && (
                    <span className="flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full shadow-sm">
                      {dmUnread > 99 ? '99+' : dmUnread}
                    </span>
                  )}
                </button>
              );
            }) : (
              <div className="px-3 py-2 text-sm text-gray-700 font-medium">No users available</div>
            )}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 w-0">
        {/* Header */}
        <header className="glass-nav border-b border-white/20">
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-white/50 rounded-xl transition-all duration-200 text-gray-700"
              aria-label="Open sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-[#3c79bb]">
                {currentRecipient ? (
                  <div className="flex items-center gap-2">
                    {currentRecipient.profileImage ? (
                      <img
                        src={getImageUrl(currentRecipient.profileImage)}
                        alt={currentRecipient.username}
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-full object-cover ring-2 ring-white/50"
                  />
                    ) : (
                      <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-xs sm:text-sm font-semibold ring-2 ring-white/50">
                        {(currentRecipient.firstName && currentRecipient.firstName[0]) || (currentRecipient.username && currentRecipient.username[0]) || '?'}
                      </div>
                    )}
                    <span className="truncate text-lg sm:text-xl lg:text-2xl">{currentChatName}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FiHash className="text-lg sm:text-xl" />
                    <span className="truncate text-lg sm:text-xl lg:text-2xl">{currentChatName}</span>
                  </div>
                )}
              </h1>
              <p className="text-xs sm:text-sm text-gray-700 mt-1 font-medium">
                {currentRecipient ? 'Direct message' : 'Channel conversation'}
              </p>
            </div>
          </div>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4">
            {messages && messages.length > 0 ? messages.map((msg, index) => {
              const msgUserId = msg.userId != null ? parseInt(String(msg.userId), 10) : NaN;
              const myId = user?.id != null ? parseInt(String(user.id), 10) : NaN;
              const isOwnMessage =
                (!Number.isNaN(msgUserId) && !Number.isNaN(myId) && msgUserId === myId) ||
                (msg.user && msg.user === user?.username);
              const displayName = getDisplayName(msg);
              const persisted = isPersistedMessageId(msg.id);
              const canEdit = isOwnMessage && persisted;
              const canDelete = (isOwnMessage || user?.role === 'admin') && persisted;
              const isEditing = editingMessageId === msg.id;

              return (
                <div
                  key={msg.id || `msg-${msg.userId}-${msg.timestamp}-${index}`}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} items-end gap-2 group/msg`}
                >
                  {!isOwnMessage && (
                    <div className="flex-shrink-0">
                      {msg.profileImage ? (
                        <img
                          src={getImageUrl(msg.profileImage)}
                          alt={displayName}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-xs font-semibold">
                          {(displayName && displayName[0]) || '?'}
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl ${
                      isOwnMessage
                        ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                        : 'glass-card text-gray-900 border border-white/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-sm font-bold ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                        {displayName}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isOwnMessage ? 'bg-white bg-opacity-30 text-white' : 'bg-gray-300 text-gray-900'}`}>
                        {msg.role}
                      </span>
                      {msg.edited_at && (
                        <span className={`text-[10px] font-semibold ${isOwnMessage ? 'text-white/80' : 'text-gray-600'}`}>
                          (edited)
                        </span>
                      )}
                      {(canEdit || canDelete) && !isEditing && (
                        <span className={`ml-auto flex items-center gap-1 ${isOwnMessage ? '' : 'opacity-0 group-hover/msg:opacity-100'} transition-opacity`}>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => startEdit(msg)}
                              className={`p-1 rounded-lg ${isOwnMessage ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-200 text-gray-700'}`}
                              title="Edit message"
                              aria-label="Edit message"
                            >
                              <FiEdit2 className="text-sm" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => deleteMessage(msg.id)}
                              className={`p-1 rounded-lg ${isOwnMessage ? 'hover:bg-white/20 text-white' : 'hover:bg-red-100 text-red-600'}`}
                              title="Delete message"
                              aria-label="Delete message"
                            >
                              <FiTrash2 className="text-sm" />
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2 mt-1">
                        <textarea
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          rows={3}
                          className={`w-full text-sm rounded-xl px-3 py-2 border resize-y min-h-[4rem] ${
                            isOwnMessage
                              ? 'bg-white/15 border-white/40 text-white placeholder-white/60'
                              : 'bg-white/80 border-gray-300 text-gray-900'
                          }`}
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${
                              isOwnMessage ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(msg.id)}
                            className="text-xs px-3 py-1.5 rounded-lg font-semibold bg-white text-[#3c79bb] hover:bg-gray-100"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm sm:text-base ${isOwnMessage ? 'text-white' : 'text-gray-900'} font-medium whitespace-pre-wrap break-words`}>
                        {renderMessageWithMentions(msg.message, isOwnMessage)}
                      </p>
                    )}
                    <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white text-opacity-90' : 'text-gray-700'} font-medium`}>
                      {formatTime(msg.timestamp || msg.created_at)}
                    </p>
                  </div>
                  {isOwnMessage && (
                    <div className="flex-shrink-0">
                      {user?.profileImage ? (
                        <img
                          src={getImageUrl(user.profileImage)}
                          alt={user.username}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-[#3c79bb] flex items-center justify-center text-white text-xs font-semibold">
                          {user?.username?.[0]?.toUpperCase() || <FiUser />}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="text-center text-gray-700 py-8">
                <p className="font-medium">No messages yet. Start the conversation!</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="glass-nav border-t border-white/20 p-2 sm:p-4">
          <form onSubmit={sendMessage} className="relative flex gap-2 sm:gap-3">
            {mentionCandidates.length > 0 && (
              <ul
                className="absolute bottom-full left-0 right-12 sm:right-16 mb-1 max-h-48 overflow-y-auto rounded-xl border border-white/30 bg-white/95 shadow-lg z-10 py-1"
                role="listbox"
              >
                {mentionCandidates.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-[#3c79bb]/10 flex items-center gap-2"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertMention(u)}
                    >
                      <span className="font-semibold text-[#3c79bb]">@{u.username}</span>
                      <span className="text-gray-600 truncate">
                        {u.firstName || u.lastName
                          ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                          : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={
                currentRecipient
                  ? `Message ${currentChatName}...`
                  : `Message #${currentChatName} (use @username to mention)...`
              }
              autoComplete="off"
              className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-600 font-medium text-sm sm:text-base"
            />
            <button
              type="submit"
              className="flex-shrink-0 bg-[#3c79bb] text-white px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] focus:outline-none focus:ring-2 focus:ring-[#3c79bb] focus:ring-offset-2 transition-all duration-200 flex items-center gap-2 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold text-sm sm:text-base"
            >
              <FiSend className="text-base sm:text-lg" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </form>
        </div>
      </div>

      {/* Create Channel Modal */}
      {showChannelModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={() => setShowChannelModal(false)}
        >
          <div 
            className="glass-strong rounded-3xl shadow-2xl max-w-md w-full animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Create Channel</h2>
            </div>
            <form onSubmit={createChannel} className="p-5 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-800 mb-2">
                  Channel Name *
                </label>
                <input
                  type="text"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  placeholder="e.g. marketing, support"
                  required
                  className="w-full px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-800 mb-2">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={newChannelDesc}
                  onChange={(e) => setNewChannelDesc(e.target.value)}
                  placeholder="What's this channel about?"
                  className="w-full px-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-500"
                />
              </div>
              <div className="flex gap-3 sm:gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#3c79bb] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 text-sm sm:text-base"
                >
                  Create Channel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChannelModal(false);
                    setNewChannelName('');
                    setNewChannelDesc('');
                  }}
                  className="flex-1 glass text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:glass-strong transition-all duration-200 font-semibold text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      </div>
    </div>
  );
}

export default Chat;
